import * as React from 'react';

// Components
import ConnectWithButton from './ConnectWithButton';

// Hooks
import { useConnectWithApple } from '../../../auth-provider/connect-with-apple/hook';
import { useThirdPartyLoginFlowEvent } from '../flow-event';
import { useAutoRestoreState } from '@tob/shared/utils/react-helper';

// Utils
import { isWebBridgeSupported } from '@shopee-user/platform';

// Constants & Enums
import { AuthenticateChannel } from '../../../core/types';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/constants';

// Types
import type { ConnectWithButtonProps } from './ConnectWithButton';
import type { WithUserWebViewProps } from '@shopee-user/with-web-view';

import IconConnectWithApple from '@shopee-user/icons/IconConnectWithApple';
import styles from './style.scss';

type Props = Omit<ConnectWithButtonProps, 'channel'> & {
  entryPoint: ThirdpartyBindingEntryPoint;
  callbackUrl?: string;
  webViewAuthWith3PServiceByToken?: WithUserWebViewProps['webViewAuthWith3PServiceByToken'];
  onAuthenticated?: ({
    idToken,
    appClientId,
  }: {
    idToken: string;
    appClientId?: string;
  }) => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  onError?: ({
    error,
    errorMsg,
  }: {
    error: number;
    errorMsg?: string;
  }) => void | Promise<void>;
  isAutoTriggered?: boolean;
};

export default function ConnectWithApple({
  className,
  label,
  layout,
  callbackUrl,
  webViewAuthWith3PServiceByToken,
  onAuthenticated,
  onCancel,
  onError,
  entryPoint,
  isAutoTriggered = false,
}: Props) {
  const [isDisabled, setIsDisabled] = useAutoRestoreState(false, {
    timeout: 3000,
  });
  const { authenticate, isReady } = useConnectWithApple({
    webViewAuthWith3PServiceByToken,
    entryPoint,
  });
  const isInited = React.useRef(false);

  const { trackEvent: trackLoginEvent } = useThirdPartyLoginFlowEvent();

  React.useEffect(() => {
    if (isInited.current) return;
    isInited.current = true;
    trackLoginEvent('onThirdPartyLoginComponentView', {
      channel: AuthenticateChannel.Apple,
    });
    if (isAutoTriggered) {
      startAuthentication();
    }
  }, []);

  const startAuthentication = React.useCallback(async () => {
    trackLoginEvent('onThirdPartyLoginClick', {
      channel: AuthenticateChannel.Apple,
    });

    setIsDisabled(true);
    let result: {
      isCancel?: boolean;
      error?: number;
      errorMsg?: string;
      idToken?: string;
      appClientId?: string;
    };
    try {
      result = await authenticate();
    } catch (e) {
      setIsDisabled(false);
      await onError?.({
        error: 1,
        errorMsg: e instanceof Error ? e.message : String(e),
      });
      return;
    }
    const { isCancel, error, errorMsg, idToken, appClientId } = result;

    if (isCancel) {
      await onCancel?.();
      setIsDisabled(false);
      return;
    }

    if (error) {
      await onError?.({
        error,
        errorMsg,
      });
      setIsDisabled(false);
      return;
    }

    setIsDisabled(false);
    // On web, the page redirects to the provider and control never returns here —
    // the browser navigates away. Only the App (via web bridge) completes the
    // OAuth flow in-page and delivers an idToken back to this callback.
    if (isWebBridgeSupported()) {
      if (idToken) {
        await onAuthenticated?.({ idToken, appClientId });
      } else {
        await onError?.({
          error: 1,
          errorMsg: 'Missing idToken',
        });
      }
    }
  }, [
    authenticate,
    callbackUrl,
    onAuthenticated,
    onCancel,
    onError,
    setIsDisabled,
    trackLoginEvent,
  ]);

  return (
    <ConnectWithButton
      channel={AuthenticateChannel.Apple}
      className={className}
      disabled={isDisabled || !isReady}
      label={label}
      icon={<IconConnectWithApple className={styles.icon} />}
      layout={layout}
      onClick={startAuthentication}
    />
  );
}
